import { useCallback, useEffect, useRef, useState } from "react";
import { MATOMO_CONFIGS } from "src/configs/matomo";
import { MatomoContext } from "src/contexts/matomo";
import { MatomoTracker } from "src/matomo/MatomoTracker";
import { useOidcUser } from "src/services/oidc-exporter";
import { isTest } from "src/settings";
import { initializeLocaleConfig } from "src/utils/dayjs";

export function MatomoProvider({ children }: { children: React.ReactNode }) {
	const { oidcUser } = useOidcUser();
	const initializedRef = useRef<boolean>(false);

	const [matomoContextValue, setMatomoContextValue] = useState<{
		matomoTracker: MatomoTracker | null;
	}>({ matomoTracker: null });

	const getUserId = useCallback(() => {
		if (oidcUser) {
			const { email, sub } = oidcUser;
			if (email && sub) {
				const domain = email.split("@")?.[1] ?? "-";
				return `${sub}@${domain}`;
			}
		}
		return undefined;
	}, [oidcUser]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: initialize matomo tracker only once
	useEffect(() => {
		if (!initializedRef.current && !isTest()) {
			try {
				const matomoTracker = new MatomoTracker({
					...MATOMO_CONFIGS,
					userId: getUserId(),
				});

				setMatomoContextValue({ matomoTracker });

				initializedRef.current = true;
			} catch (error) {
				console.error("Failed to initialize Matomo:", error);
			}
		}
	}, []);

	useEffect(() => {
		try {
			if (!matomoContextValue?.matomoTracker?.mutationObserver) {
				matomoContextValue?.matomoTracker?.trackPageView();
				matomoContextValue?.matomoTracker?.trackEvents();

				initializeLocaleConfig().catch((error) =>
					console.error("failed to initiate locale config", error),
				);
			}

			const userId = getUserId();

			if (userId) {
				matomoContextValue?.matomoTracker?.setUserId(userId);
			}
		} catch (error) {
			console.error("Error in Matomo tracking:", error);
		}
	}, [matomoContextValue, getUserId]);

	return (
		<MatomoContext.Provider value={matomoContextValue}>
			{children}
		</MatomoContext.Provider>
	);
}
